<template>
  <Layout
    title="Start your 5-day free trial"
    :subtitle="title"
    reverse-title-subtitle
    class="payment-capture-20676"
  >
    <LoadingSpinner v-if="!billing || !plan" />
    <template v-else>
      <BookCoverStack :book="book" class="payment-capture-20676__book-stack" />

      <PaymentStep20676
        :billing="billing"
        :plan="plan"
        @saved="onSaved"
        @skip="onSkip"
      />
    </template>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import type { PaymentCapture20676Page } from '@/models/onboarding.types'
import type { BillingView, PlanView } from '@/models/billing'
import { createBilling } from '@/models/billing'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'
import {
  getCachedBilling20676,
  getInterestBook20676,
  getResolvedTitle20676,
  getSelectedPlanId20676,
} from '@/models/onboarding.plan-card-capture-20676'
import { DEFAULT_INTEREST_BOOK } from '@/helpers/bookCoverStack'

import Layout from '@/app/components/onboarding/Layout.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import BookCoverStack from '@/app/components/book/BookCoverStack.vue'
import PaymentStep20676 from '@/app/components/onboarding/plan-card-capture/PaymentStep20676.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as PaymentCapture20676Page
})

// PlanSelection20676 resolves the personalized/generic title; fall back to
// the static page config placeholder only if that failed to resolve the title.
// This screen is only reachable from the PlanSelection20676 page,
// so we can rely on that page having already resolved the title.
const title = computed(() => getResolvedTitle20676() ?? page.value.title)

// Same reasoning -- PlanSelection20676 always bridges a book (the resolved
// one, or the default), but fall back defensively in case it didn't.
const book = computed(() => getInterestBook20676() ?? DEFAULT_INTEREST_BOOK)

const billing = ref<Raw<BillingView> | null>(null)
const plan = ref<Raw<PlanView> | null>(null)

onBeforeMount(async () => {
  const cachedBilling = getCachedBilling20676()
  if (cachedBilling) {
    // Set by PlanSelection20676.
    billing.value = cachedBilling
  } else {
    // Defensive fallback in case the cached billing is not available.
    // It's possible if the user refreshes the page on the 2nd billing screen and we
    // lose the "cached" in-memory state.
    try {
      const response = await backend.getBilling()
      billing.value = await createBilling(response)
    } catch (error) {
      Sentry.captureException(error)
      // If billing can't load, treat it like a skip.
      props.view.goNextPage()
      return
    }
  }

  const selectedPlanId = getSelectedPlanId20676()
  plan.value =
    billing.value.plans.find((p) => p.id === selectedPlanId) ??
    billing.value.annualPlan
})

function onSaved(): void {
  props.view.goNextPage()
}

function onSkip(): void {
  Mixpanel.trackOnboardingBillingSkipClick(props.view.currentPage.id)
  props.view.goNextPage()
}
</script>

<style scoped lang="scss">
.payment-capture-20676 {
  &__book-stack {
    margin-bottom: 24px;
    margin-top: 8px;
  }
}
</style>
